import {useCallback, useEffect, useMemo, useState} from "react";
import {useIntl} from "react-intl";
import {ILinkData} from "@housebets/housebets-x-package/lib/global/links.types";
import {useLocation} from "react-router-dom";
import {notify} from "@housebets/housebets-x-package";

import {useAppSelector} from "hooks/useAppSelector";
import {fileToBase64, getAvatarUrl} from "app/utils/help";
import {setWalletType} from "app/store/slices/setting";
import {WalletType} from "app/types/common";
import useLiveChat from "app/hooks/useLiveChat";
import apiUser from "app/api/user";

import {useStationMessage} from "./useStationMessage";
import { useDispatch } from "react-redux";
import { useLangNavigate } from "app/hooks/useLangNavigate";

export const useProfileDropdownData = () => {
    const platformConfig = useAppSelector((state) => state.config.platformConfig);
    const userData = useAppSelector((state) => state.user.user);
    const isAuth = useAppSelector((state) => state.user.isLogin);
    const {messageTotalCount} = useStationMessage();
    const {showLiveChat} = useLiveChat();
    const location = useLocation();
    const {$t} = useIntl();
    const langNavigate = useLangNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenRedeemModal, setIsOpenRedeemModal] = useState(false);
    const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

    const mutatedUserData = useMemo(() => {
        const vipList = platformConfig?.vipList || [];
        const currentLevel = vipList?.find((v) => v.level === userData?.level);

        return {
            avatar: getAvatarUrl(userData?.header || ""),
            id: userData?.userId + "" || "",
            name: userData?.nick || "",
            level: currentLevel?.name,
        };
    }, [userData, platformConfig]);

    const loggedTextData = useMemo(() => {
        const vipList = platformConfig?.vipList || [];

        const currentLevel = vipList?.find((v) => v.level === userData?.level);
        const nextLevel = vipList?.find((v) => v.level === (userData?.level || 0) + 1);
        const levelTitle = currentLevel?.name;
        const levelNextTitle = nextLevel?.name;

        return {
            name: userData?.nick || "",
            level: userData?.exp || 0,
            maxLevel: nextLevel?.exp || userData?.exp || 0,
            levelTitle: levelTitle || "",
            levelNextTitle: levelNextTitle || "",
            levelNumber: userData?.level,
            amount: userData?.exp || 0,
        };
    }, [userData, platformConfig, isAuth]);

    const links: ILinkData[] = useMemo(() => {
        return [
            {
                id: "Notifications",
                href: langNavigate("/notifications"),
                icon: "Notifications",
                name: $t({id: "Notifications"}),
                count: messageTotalCount,
            },
            {
                id: "userinfo",
                href: "",
                icon: "AccountIcon",
                name: $t({id: "UserInfo" }),
                onClick: (e) => {
                    e?.preventDefault();
                    setIsUserInfoModalOpen(true);
                },
            },
            {
                id: "Wallet",
                href: langNavigate("/"),
                icon: "Wallet",
                name: $t({id: "Wallet"}),
                onClick: () => {
                    dispatch(setWalletType(WalletType.Deposit));
                },
            },
            {
                id: "Vip",
                href: langNavigate("/vip"),
                icon: "Vip",
                name: $t({id: "Rewards"}),
            },
            {
                id: "RedeemCode",
                href: langNavigate("/"),
                icon: "Link",
                name: $t({id: "RedeemCode"}),
                onClick: (e) => {
                    e?.preventDefault();
                    setIsOpenRedeemModal(true);
                },
            },
            {
                id: "AffilateProgramm",
                href: langNavigate("/affiliate"),
                icon: "AffilateProgramm",
                name: $t({id: "Affilate"}),
            },
            {
                id: "Transactions",
                href: langNavigate("/transactions"),
                icon: "Transactions",
                name: $t({id: "Transactions"}),
            },
            {
                id: "Settings",
                href: langNavigate("/settings"),
                icon: "Settings",
                name: $t({id: "Settings"}),
            },
            {
                id: "LiveSupport",
                href: langNavigate("/"),
                icon: "LiveSupport",
                name: $t({id: "LiveSupport"}),
                onClick: () => {
                    showLiveChat();
                },
            },
            {
                id: "LogOut",
                href: langNavigate("/"),
                icon: "LogOut",
                name: $t({id: "LogOut"}),
                onClick: () => {
                    setIsLogoutModalOpen(true);
                },
            },
        ];
    }, [messageTotalCount, $t]);

    // TODO: need integration with the backend
    const onFileAdd = useCallback(async (file: File | null) => {
        if (!file) return;
        try {
            const base64 = await fileToBase64(file);
            const {status, data} = await apiUser.modifyUserHead(base64?.toString().split(",").reverse()[0] || "");
            if (status === 200 && data.code === 0) {
                notify({
                    type: "success",
                    title: "Avatar updated successfully",
                });
            } else {
                notify({
                    type: "error",
                    title: "SystemError",
                });
            }
        } catch (e) {
            notify({
                type: "error",
                title: "SystemError",
            });
        }
    }, []);

    // TODO: need integration with the backend
    const onFileTypeError = useCallback(() => {
        notify({
            type: "error",
            title: "SystemError",
        });
    }, []);

    useEffect(() => {
        setIsOpen(false);
        setIsUserInfoModalOpen(false);
    }, [location]);

    return {
        loggedTextData,
        isOpen,
        setIsOpen,
        mutatedUserData,
        messageTotalCount,
        links,
        isOpenRedeemModal,
        setIsOpenRedeemModal,
        onFileAdd,
        onFileTypeError,
        isUserInfoModalOpen,
        setIsUserInfoModalOpen,
        isLogoutModalOpen,
        setIsLogoutModalOpen,
    };
};

